import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { COMMON, TYPOGRAPHY } from "@tKgpco/theme/design";
import breakpointValuesMediaQueries from "@tKgpco/utilities/breakpointValuesMediaQueries";
import updateCssValue, { Operators } from "@tKgpco/utilities/updateCssValue";
import last from "lodash/last";
import { css } from "styled-components";

const _contentSpacingStyles = (
    top: string | 0 | null = 0,
    bottom: string | 0 | null = TYPOGRAPHY.spacing.base,
    unit: string,
    override?: boolean,
) => {
    return css`
        ${top !== null
            ? `${unit}-top: ${top}; ${top !== 0 ? `&:first-child { ${unit}-top: ${override ? top : 0}; }` : ""}`
            : ""}
        ${bottom !== null
            ? `${unit}-bottom: ${bottom}; ${
                  bottom !== 0 ? `&:last-child { ${unit}-bottom: ${override ? bottom : 0}; }` : ""
              }`
            : ""}
    `;
};

export const contentSpacingStyles = (
    top: string | 0 | null = 0,
    bottom: string | 0 | null = TYPOGRAPHY.spacing.base,
    unit = "margin",
) => {
    return _contentSpacingStyles(top, bottom, unit, false);
};

export const overrideSpacingStyles = (
    top: string | 0 | null = 0,
    bottom: string | 0 | null = TYPOGRAPHY.spacing.base,
    unit = "margin",
) => {
    return _contentSpacingStyles(top, bottom, unit, true);
};

export const siteWidthStyles = (sideSpacing?: Array<string | null>) => {
    const _sideSpacing = sideSpacing ?? COMMON.spacing.side;

    return css`
        max-width: ${props => last(props.theme.breakpoints.maxWidths)}px;
        margin-right: auto;
        margin-left: auto;
        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [{ values: _sideSpacing, properties: ["padding-right", "padding-left"] }],
                mediaOptions.min,
            )}
    `;
};

export const siteWidthForFixed = (sideSpacing?: Array<string | null>) => {
    const _sideSpacing = sideSpacing ?? COMMON.spacing.side;
    return css`
        left: 30px !important;
        right: 30px !important;
    `;
};

export const widgetWrapperStyles = () => {
    return css`
        ${contentSpacingStyles()}
    `;
};

export const clearfixStyles = () => {
    return css`
        &::before,
        &::after {
            content: "";
            display: table;
        }

        &::after {
            clear: both;
        }
    `;
};

export const scalableIconStyles = () => {
    return css`
        position: relative;
        width: 1em;
        height: 1em;

        svg {
            width: inherit;
            height: inherit;
        }
    `;
};

export const sideBoxStyles = (padding: Array<string | null> = ["1.5em", null, null, null, "2em"]) => {
    return css`
        width: 100%;
        box-shadow: ${getProp("theme.shadows.small")};
        ${props =>
            breakpointValuesMediaQueries(props.theme, [{ values: padding, properties: ["padding"] }], mediaOptions.min)}
    `;
};

// -- GRID

export const gridGapStyles = (
    horizontalGap: Array<string | 0 | null>,
    verticalGap?: Array<string | 0 | null> | null,
) => {
    const _horizontalGap = horizontalGap.map(x => updateCssValue(x, Operators.Divide, 2));
    const _verticalGap = verticalGap?.map(x => updateCssValue(x, Operators.Divide, 2)) ?? _horizontalGap;

    return css`
        > ${GridOffset} {
            width: auto !important; /* TODO: Remove "!important" once GridOffset issues are fixed */
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        {
                            values: _horizontalGap.map(x => (x ? `-${x}` : x)),
                            properties: ["margin-right", "margin-left"],
                        },
                        {
                            values: _verticalGap.map(x => (x ? `-${x}` : x)),
                            properties: ["margin-top", "margin-bottom"],
                        },
                    ],
                    mediaOptions.min,
                )}

            > ${GridItemStyle} {
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            { values: _horizontalGap, properties: ["padding-right", "padding-left"] },
                            { values: _verticalGap, properties: ["padding-top", "padding-bottom"] },
                        ],
                        mediaOptions.min,
                    )}
            }
        }
    `;
};
